import { Component, DoCheck } from '@angular/core';
import { MsImageViewerService } from './ms-image-viewer.service';

/**
 * Trình xem ảnh toàn màn hình, tự viết.
 *
 * VÌ SAO KHÔNG DÙNG ngx-ionic-image-viewer Ở ĐÂY:
 * thư viện đó mở ảnh bằng một ion-modal riêng. Khi ảnh được mở từ bên trong
 * một ion-modal khác (ví dụ hộp thoại làm bài tập) thì thành modal lồng modal —
 * đóng ảnh xong, hộp thoại bên dưới mất khả năng cuộn và cú vuốt lại rơi vào
 * lớp phủ còn sót, làm đóng luôn hộp thoại.
 *
 * Component này KHÔNG dùng modal. Nó chỉ là một lớp phủ, nên không có gì
 * chồng lên nhau và không đụng tới trạng thái của modal cha.
 *
 * CÁCH ĐẶT: chỉ đặt ĐÚNG MỘT thẻ, trong app.component.html, ngay dưới
 * <ion-router-outlet>. Các trang không đặt thẻ này, mà gọi MsImageViewerService.
 *
 * Vì sao phải đặt ở gốc: .ion-page và ion-router-outlet đều tạo ngữ cảnh xếp
 * lớp riêng (z-index: 0). Thẻ nằm bên trong chúng thì dù z-index bao nhiêu
 * cũng không nổi lên trên modal hay thanh tab dưới được. Đặt ở cấp <ion-app>
 * là đúng chỗ Ionic gắn modal, nên z-index mới có tác dụng thật.
 */
@Component({
  selector: 'ms-image-viewer',
  templateUrl: './ms-image-viewer.component.html',
  styleUrls: ['./ms-image-viewer.component.scss'],
})
export class MsImageViewerComponent implements DoCheck {
  get isOpen(): boolean {
    return this.viewer.isOpen;
  }

  get images(): string[] {
    return this.viewer.images;
  }

  get index(): number {
    return this.viewer.index;
  }

  set index(v: number) {
    this.viewer.index = v;
  }

  scale = 1;
  tx = 0;
  ty = 0;

  /** Các ngón đang chạm, theo pointerId */
  private pointers = new Map<number, { x: number; y: number }>();
  private startDist = 0;
  private startScale = 1;
  private startTx = 0;
  private startTy = 0;
  private startMid = { x: 0, y: 0 };
  private lastTap = 0;

  private readonly MAX_SCALE = 4;

  /** Phiên bản lần mở gần nhất mà component đã xử lý */
  private seenVersion = -1;

  constructor(public viewer: MsImageViewerService) { }

  ngDoCheck() {
    // Mỗi lần service mở ảnh mới thì trả mức phóng to về ban đầu
    if (this.viewer.version !== this.seenVersion) {
      this.seenVersion = this.viewer.version;
      this.resetZoom();
    }
  }

  get current(): string {
    return this.images && this.images.length ? this.images[this.index] : '';
  }

  get hasMany(): boolean {
    return !!this.images && this.images.length > 1;
  }

  close() {
    this.resetZoom();
    this.viewer.close();
  }

  prev() {
    if (!this.hasMany) {
      return;
    }
    this.index = (this.index - 1 + this.images.length) % this.images.length;
    this.resetZoom();
  }

  next() {
    if (!this.hasMany) {
      return;
    }
    this.index = (this.index + 1) % this.images.length;
    this.resetZoom();
  }

  /** Bấm vào nền tối thì đóng, nhưng chỉ khi đang không phóng to */
  onBackdrop(ev: Event) {
    if (this.scale <= 1.02) {
      ev.stopPropagation();
      this.close();
    }
  }

  // ----- Chạm: chụm để phóng, kéo để di chuyển, chạm hai lần để phóng nhanh -----

  onPointerDown(ev: PointerEvent) {
    (ev.target as HTMLElement).setPointerCapture?.(ev.pointerId);
    this.pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

    if (this.pointers.size === 2) {
      const [a, b] = Array.from(this.pointers.values());
      this.startDist = this.distance(a, b);
      this.startScale = this.scale;
      this.startTx = this.tx;
      this.startTy = this.ty;
      this.startMid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    } else if (this.pointers.size === 1) {
      this.startTx = this.tx;
      this.startTy = this.ty;
      this.startMid = { x: ev.clientX, y: ev.clientY };
      this.handleDoubleTap();
    }
  }

  onPointerMove(ev: PointerEvent) {
    if (!this.pointers.has(ev.pointerId)) {
      return;
    }
    this.pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });

    if (this.pointers.size === 2) {
      const [a, b] = Array.from(this.pointers.values());
      const dist = this.distance(a, b);
      if (this.startDist > 0) {
        const next = (dist / this.startDist) * this.startScale;
        this.scale = Math.min(this.MAX_SCALE, Math.max(1, next));
      }
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      this.tx = this.startTx + (mid.x - this.startMid.x);
      this.ty = this.startTy + (mid.y - this.startMid.y);
      ev.preventDefault();
    } else if (this.pointers.size === 1 && this.scale > 1.02) {
      // Chỉ cho kéo khi đang phóng to, để lúc thường còn vuốt đóng được
      this.tx = this.startTx + (ev.clientX - this.startMid.x);
      this.ty = this.startTy + (ev.clientY - this.startMid.y);
      ev.preventDefault();
    }
  }

  onPointerUp(ev: PointerEvent) {
    this.pointers.delete(ev.pointerId);
    if (this.pointers.size < 2) {
      this.startDist = 0;
    }
    if (this.scale <= 1.02) {
      this.resetZoom();
    }
  }

  private handleDoubleTap() {
    const now = Date.now();
    if (now - this.lastTap < 300) {
      this.scale = this.scale > 1.02 ? 1 : 2.5;
      this.tx = 0;
      this.ty = 0;
      this.lastTap = 0;
    } else {
      this.lastTap = now;
    }
  }

  private resetZoom() {
    this.scale = 1;
    this.tx = 0;
    this.ty = 0;
    this.pointers.clear();
    this.startDist = 0;
  }

  private distance(a: { x: number; y: number }, b: { x: number; y: number }): number {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
  }
}
